import axios from "axios";
import conf from "../conf.json";
import { clearSiteData, logout } from "./session";

const API_BASE_URL =
	process.env.NODE_ENV === "development"
		? conf.REACT_API_BASE_URL_DEV
		: conf.REACT_API_BASE_URL;

// Create an Axios instance
const apiClient = axios.create({
	baseURL: API_BASE_URL,
});

// Add a request interceptor to include Authorization header
apiClient.interceptors.request.use((config) => {
	const auth = localStorage.getItem("auth");
	if (auth) {
		config.headers.Authorization = `Basic ${auth}`;
	}
	return config;
});

// Any 401 means the stored credentials are gone or no longer valid: wipe the
// session and send the user back to the login screen. Requests that opt out
// (the login call itself) surface the error to their caller instead.
apiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
			await logout();
		}
		return Promise.reject(error);
	}
);

// The API converts every money figure into the requested display currency, so
// the client never multiplies by a rate itself. USD is the stored base and the
// server's default, so it is left off the query string to keep one cache entry.
const currencyParams = (currency) => {
	const code = String(currency || "").trim().toUpperCase();
	return code && code !== "USD" ? { currency: code } : {};
};

// Define API functions
export const fetchData = async () => {
	const response = await apiClient.get("/data");
	return response.data;
};

// Define API functions
export const fetchRealtime = async () => {
	const response = await apiClient.get("/data/realtime");
	return response.data;
};

export const updateInvestment = async (id, investment) => {
	await apiClient.put(`/investment/${id}`, investment);
};

export const postInvestment = async (investmentForm) => {
	await apiClient.post("/investment", investmentForm);
};

export const copyInvestmeents = async (data) => {
	await apiClient.put("/copy-investments", data);
};

export const getDataByDate = async (date) => {
	const response = await apiClient.get(`/days/${date}`);
	return response.data;
};

export const login = async ({ username, password }) => {
	const credentials = btoa(`${username}:${password}`);
	// Start from a clean slate so nothing from a previous session survives.
	await clearSiteData();
	const response = await apiClient.get("/assets", {
		headers: { Authorization: `Basic ${credentials}` },
		skipAuthRedirect: true,
	});
	localStorage.setItem("auth", credentials); // Store the credentials for future requests
	return response.data;
};

export const getMaxMinData = async () => {
	const response = await apiClient.get("/data/max-min");
	return response.data;
};

//v2

export const fetchInvestments = async (currency) => {
	const response = await apiClient.get("/investments", {
		params: currencyParams(currency),
	});
	if (response.status !== 200) {
		throw new Error("Failed to fetch investments");
	}
	return response.data;
};
export const postInvestments = async ({
	asset,
	value,
	units,
	currency,
	transactionType,
	transactionDate,
}) => {
	const response = await apiClient.post("/investments", {
		asset,
		value,
		units,
		currency: currency || "USD", // Default to USD if no currency is provided
		transactionType: transactionType || "BUY",
		transactionDate: transactionDate || new Date().toISOString(),
	});
	if (response.status !== 201) {
		throw new Error("Failed to create investment");
	}
	return response.data;
};

export const updateInvestments = async ({
	id,
	asset,
	value,
	units,
	currency,
	transactionType,
	transactionDate,
}) => {
	const response = await apiClient.put(`/investments/${id}`, {
		asset,
		value,
		units,
		currency: currency || "USD",
		transactionType: transactionType || "BUY",
		transactionDate,
	});
	if (response.status !== 200) {
		throw new Error("Failed to update investment");
	}
	return response.data;
};

// No status check: a successful delete replies 204 with no body.
export const deleteInvestments = async (id) => {
	await apiClient.delete(`/investments/${id}`);
	return true;
};

export const fetchInvestmentSummary = async (asOfDate, currency) => {
	const response = await apiClient.get("/investments/summary", {
		params: { ...(asOfDate ? { asOfDate } : {}), ...currencyParams(currency) },
	});
	if (response.status !== 200) {
		throw new Error("Failed to fetch investment summary");
	}
	return response.data;
};

export const fetchDividends = async (currency) => {
	const response = await apiClient.get("/dividends", {
		params: currencyParams(currency),
	});
	if (response.status !== 200) {
		throw new Error("Failed to fetch dividends");
	}
	return response.data;
};
export const postDividend = async ({ asset, amount, currency, date }) => {
	const response = await apiClient.post("/dividends", {
		asset,
		amount,
		currency: currency || "USD",
		date,
	});
	if (response.status !== 201) {
		throw new Error("Failed to create dividend");
	}
	return response.data;
};
export const fetchCurrencies = async () => {
	const response = await apiClient.get("/exchange");
	if (response.status !== 200) {
		throw new Error("Failed to fetch currencies");
	}
	return response.data;
};
export const fetchDaily = async (currency) => {
	const response = await apiClient.get("/daily", {
		params: currencyParams(currency),
	});
	if (response.status !== 200) {
		throw new Error("Failed to fetch daily");
	}
	return response.data;
};

export const fetchAssetByDate = async (asset, date, currency) => {
	const response = await apiClient.get(`/daily/${asset}/${date}`, {
		params: currencyParams(currency),
	});
	if (response.status !== 200) {
		throw new Error("Failed to fetch asset by date");
	}
	return response.data;
};
export const fetchAllAssetLatestPrice = async (currency) => {
	const response = await apiClient.get(`/daily/assets`, {
		params: currencyParams(currency),
	});
	if (response.status !== 200) {
		throw new Error("Failed to fetch asset by date");
	}
	return response.data;
};
export const fetchAssets = async (asset, date) => {
	const response = await apiClient.get(`/assets`);
	if (response.status !== 200) {
		throw new Error("Failed to fetch assets");
	}
	return response.data;
};

export const fetchGoals = async () => {
	const response = await apiClient.get("/goals");
	if (response.status !== 200) {
		throw new Error("Failed to fetch goals");
	}
	return response.data;
};
export const postGoal = async ({ type, year, amount, currency, note }) => {
	const response = await apiClient.post("/goals", {
		type,
		year: Number(year),
		amount: Number(amount),
		currency: (currency || "USD").toUpperCase(),
		note: note || null,
	});
	if (response.status !== 201) {
		throw new Error("Failed to create goal");
	}
	return response.data;
};
export const updateGoal = async ({ id, year, amount, currency, note }) => {
	const response = await apiClient.put(`/goals/${id}`, {
		year: Number(year),
		amount: Number(amount),
		currency: (currency || "USD").toUpperCase(),
		note: note || null,
	});
	if (response.status !== 200) {
		throw new Error("Failed to update goal");
	}
	return response.data;
};
// No status check: a successful delete replies 204 with no body.
export const deleteGoal = async (id) => {
	await apiClient.delete(`/goals/${id}`);
	return true;
};
